import { ChangeEvent, FC, useState, useMemo, useCallback, useRef } from 'react';
import Chart from 'react-apexcharts';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  useTheme} from '@mui/material';
import ArrowRightIcon from '@/icons/ArrowRight';
import { merge } from 'lodash'
import { DatabaseResponseDto } from '@/gen/api/backup';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useMetricsContext } from '@/pages/databases/useMetricsController';
import { useEffect } from 'react';
import { TimeRangeType } from '@/gen/api/metrics';
import { ChartLegends, generalizeData, genericChartOptions, LineChartDataEx, LineChartResponseDtoEx } from './metricsChartCommon';


export interface DatabaseStatisticsProps {
  databaseInfo: DatabaseResponseDto;
}

const DatabaseStatistics: FC<DatabaseStatisticsProps> = ({
  databaseInfo,
}) => {
  const location = useLocation()
  const theme = useTheme();
  const {
    dbsizeData,
    observeDbsize,
    setTimeRange
  } = useMetricsContext()

  useEffect(() => {
    observeDbsize(true)
    setTimeRange(TimeRangeType.Week)
  }, [observeDbsize, setTimeRange])

  const finalData: LineChartResponseDtoEx = useMemo(() => generalizeData(theme, dbsizeData), [theme, dbsizeData])

  const [selectedSeries, setSelectedSeries] = useState(finalData.legend);
  const selectedSeriesRef = useRef(selectedSeries)
  useEffect(() => {
    setSelectedSeries(finalData.legend)
  }, [finalData])
  
  useEffect(() => {
    selectedSeriesRef.current = selectedSeries
  }, [selectedSeries])
  const handleChange = useCallback((x: LineChartDataEx, checked: boolean) => {
    if (!checked) {
      setSelectedSeries(selectedSeriesRef.current.filter((item) => item !== x.name));
    } else {
      setSelectedSeries([...selectedSeriesRef.current, x.name]);
    }
  }, []);

  const maxValueMap = useMemo(() => 
    finalData.series.reduce((acc, s) => {
      acc[s.name] = Math.max.apply(Math, s.data)
      return acc
    }, {} as Record<string, number>), [finalData])

  const visibleSeries = useMemo(() => finalData.series.filter((item) => item.name && selectedSeries.includes(item.name)), [finalData, selectedSeries]);

  const maxValue = visibleSeries.length > 0 ? Math.max.apply(Math, visibleSeries.map(s => maxValueMap[s.name])) : 0
  const units = ["B", "KB", "MB", "GB"]
  const [unit, offsetRank] = (() => {
    const ls = units.flatMap((u, i) => (maxValue / (1 << (i * 10))) > 1 ? [[u, i] as const] : []);
    return ls.length > 0 ? ls[ls.length - 1] : (["GB", 0] as const)
  })();
  const shrinkFactor = 1 << (offsetRank * 10)

  const genericOptions = useMemo(() => merge(genericChartOptions(theme), {
    options: {
      yaxis: [{
        title: {
          text: `DB/Table Size (${unit})`
        }
      }],
    },
  }), [theme, unit])

  const chart = useMemo(() => merge({}, genericOptions, {
    options: {
      colors: visibleSeries.map((item) => item.color),
      xaxis: {
        type: 'datetime',
        categories: finalData.xaxis,
      },
    },
    series: visibleSeries.map(s => ({
      ...s,
      data: s.data.map(d => Number((d / shrinkFactor).toFixed(1)))
    })),
  }), [genericOptions, visibleSeries, finalData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        maxHeight: 390,
      }}
    >
      <Card>
        <CardHeader
          title="Statistics"
          action={
            <Button
              color="primary"
              variant="text"
              endIcon={<ArrowRightIcon fontSize="small" />}
              component={RouterLink}
              to={`${location.pathname}?mode=metrics`}
            >
              View Metrics
            </Button>
          }
        />
        <CardContent>
          <ChartLegends series={finalData.series} onClick={handleChange} />
          <Chart
            height="268"
            type="line"
            {...chart}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DatabaseStatistics;